// Lo que la web dice de las notas generales del proyecto, en funciones puras.
//
// El cajón de la cabecera y la tarjeta de la pestaña de terreno muestran la
// MISMA línea de resumen y el MISMO contador: salen de acá para que no puedan
// contradecirse.

import type { NotasGeneralesDeTerreno } from "@/lib/terreno";

import { momentoDeSincronizacion, pluralizar } from "./registroDeTerreno";

/** El ancla de la tarjeta en la pestaña de terreno: a donde salta el cajón. */
export const ID_DE_LAS_NOTAS_GENERALES = "notas-generales-terreno";

/**
 * Baja hasta la tarjeta de notas generales de la pestaña de terreno. La pestaña
 * se monta DESPUÉS de navegar, así que se espera un tick a que exista.
 */
export function irALasNotasGenerales() {
  window.setTimeout(() => {
    document.getElementById(ID_DE_LAS_NOTAS_GENERALES)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, 80);
}

/** Cuántas notas hay: cada audio cuenta uno y el texto, si existe, otro. */
export function cantidadDeNotas(notas: NotasGeneralesDeTerreno): number {
  return notas.audios.length + (notas.texto ? 1 : 0);
}

/**
 * La última vez que algo de las notas llegó al servidor (ISO), o `null`.
 * Se comparan MOMENTOS y no cadenas: los offsets pueden diferir.
 */
export function ultimaSincronizacionDeLasNotas(notas: NotasGeneralesDeTerreno): string | null {
  const momentos = [notas.texto?.sincronizado_en, ...notas.audios.map((a) => a.sincronizado_en)]
    .filter((m): m is string => Boolean(m))
    .filter((m) => !Number.isNaN(new Date(m).getTime()));

  if (momentos.length === 0) return null;

  return momentos.reduce((a, b) => (new Date(b).getTime() > new Date(a).getTime() ? b : a));
}

/** "2 audios · texto · Sincronizado 05-07-2026 · 09:12", sin el encargado. */
export function resumenDeLasNotas(notas: NotasGeneralesDeTerreno): string {
  const partes = [
    notas.audios.length > 0 ? pluralizar(notas.audios.length, "audio", "audios") : null,
    notas.texto ? "texto" : null,
  ].filter((p): p is string => p !== null);

  const sincronizada = ultimaSincronizacionDeLasNotas(notas);
  if (sincronizada) partes.push(`Sincronizado ${momentoDeSincronizacion(sincronizada)}`);

  return partes.join(" · ");
}

/** La línea bajo el título: encargado y resumen, o el aviso de que no hay nada. */
export function metaDeLasNotas(encargado: string | null, notas: NotasGeneralesDeTerreno): string {
  if (cantidadDeNotas(notas) === 0) return "Sin notas desde terreno";

  return [encargado ?? "Sin encargado de terreno", resumenDeLasNotas(notas)].join(" · ");
}
